import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, RotateCw, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, FormDialog, PageHead, Select, useApi } from "@/components/shop/common";

const ROLES = [{ value: "service_writer", label: "Service writer" }, { value: "technician", label: "Technician" }];
const roleLabel = (r) => ({ owner: "Owner", service_writer: "Service writer", technician: "Technician" }[r] || r);
const copy = (url) => { navigator.clipboard?.writeText(url); toast.success("Invite link copied"); };

export default function Staff() {
  const { user } = useAuth();
  const [d, load] = useApi("/staff");
  const [techs] = useApi("/technicians");
  const [open, setOpen] = useState(false);
  const techOpts = (techs || []).map((t) => ({ value: t.id, label: t.name }));

  const invite = async (v) => {
    const { data } = await api.post("/staff/invites", { email: v.email, role: v.role || "service_writer", technician_id: v.technician_id || null });
    if (data.email_sent) toast.success(`Invite emailed to ${data.email}`);
    else { copy(data.join_url); toast.warning("Email couldn't be sent — the invite link was copied so you can share it"); }
    load();
  };
  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const resend = (id) => act(async () => { const { data } = await api.post(`/staff/invites/${id}/resend`); copy(data.join_url); }, "Invite re-sent");

  const used = d ? d.members.length - 1 + d.invites.length : 0;
  return (
    <>
      <PageHead eyebrow="Team" title="Staff logins">
        <Button onClick={() => setOpen(true)} className="rounded-sm hover:bg-orange-600 hover:text-white" data-testid="invite-staff-btn"><UserPlus className="h-4 w-4 mr-1" /> Invite</Button>
      </PageHead>
      {d && (
        <>
          <p className="text-sm text-muted-foreground mb-6" data-testid="staff-limit">{d.staff_limit ? `${used} of ${d.staff_limit} staff logins used on your plan.` : `${used} staff login${used === 1 ? "" : "s"} · unlimited on your plan.`} Service writers can do everything except staff and settings. Technicians work their jobs only, with no prices, invoicing, customers or stock.</p>
          <div className="border border-border rounded-sm bg-card/80 divide-y divide-border mb-8" data-testid="staff-members">
            {d.members.map((m) => (
              <div key={m.user_id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`member-row-${m.user_id}`}>
                <div className="flex-1 min-w-[200px]"><p className="font-semibold text-sm">{m.name || m.email}</p><p className="text-xs text-muted-foreground font-mono">{m.email}</p></div>
                {m.shop_role === "owner" ? <span className="font-mono text-xs uppercase text-primary">Owner{m.user_id === user.user_id && " (you)"}</span> : (
                  <>
                    <Select className="w-44" value={m.shop_role} options={ROLES} data-testid={`member-role-${m.user_id}`}
                      onChange={(e) => act(() => api.put(`/staff/members/${m.user_id}`, { shop_role: e.target.value, technician_id: m.technician_id }), "Role updated")} />
                    {m.shop_role === "technician" && (
                      <Select className="w-44" value={m.technician_id || ""} placeholder="Link tech profile" options={techOpts} data-testid={`member-tech-${m.user_id}`}
                        onChange={(e) => act(() => api.put(`/staff/members/${m.user_id}`, { shop_role: "technician", technician_id: e.target.value || null }), "Tech profile linked")} />
                    )}
                    <Button variant="ghost" size="icon" className="hover:text-red-400" data-testid={`member-remove-${m.user_id}`}
                      onClick={() => window.confirm(`Remove ${m.email} from your shop?`) && act(() => api.delete(`/staff/members/${m.user_id}`), "Removed from shop")}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            ))}
          </div>
          <h2 className="font-display text-2xl uppercase mb-3">Pending invites</h2>
          {d.invites.length === 0 ? <Empty testid="invites-empty">No pending invites.</Empty> : (
            <div className="border border-border rounded-sm bg-card/80 divide-y divide-border" data-testid="staff-invites">
              {d.invites.map((i) => (
                <div key={i.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`invite-row-${i.id}`}>
                  <div className="flex-1 min-w-[200px]"><p className="text-sm font-mono">{i.email}</p><p className="text-xs text-muted-foreground">{roleLabel(i.role)} · expires {new Date(i.expires_at).toLocaleDateString()}</p></div>
                  <Button variant="outline" size="sm" className="rounded-sm hover:text-white" onClick={() => resend(i.id)} data-testid={`invite-resend-${i.id}`}><RotateCw className="h-3.5 w-3.5 mr-1" /> Resend & copy link</Button>
                  <Button variant="ghost" size="icon" className="hover:text-red-400" onClick={() => act(() => api.delete(`/staff/invites/${i.id}`), "Invite revoked")} data-testid={`invite-revoke-${i.id}`}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <FormDialog open={open} onOpenChange={setOpen} title="Invite staff" testid="invite-dialog" initial={{ role: "service_writer" }} onSubmit={invite}
        fields={[{ name: "email", label: "Email", type: "email", required: true }, { name: "role", label: "Role", type: "select", options: ROLES, required: true },
          { name: "technician_id", label: "Tech profile (technicians only)", type: "select", options: [{ value: "", label: "None" }, ...techOpts] }]} />
    </>
  );
}
